import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  CreditCard,
  RefreshCw,
  FlaskConical,
} from 'lucide-react';
import {
  REBILL_PUBLIC_KEY,
  REBILL_PLAN_ID,
} from '../config';
import { ensureRebillSDK, buildInstantPlan } from '../lib/rebill';

// El SDK de Rebill son web components (custom elements). Declaramos el tag
// para que TSX/React lo acepte.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'rebill-checkout': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

// Monto de la suscripción de prueba (ARS, recurrente mensual).
const TOTAL_ARS = 10000;

const hasKey = !!REBILL_PUBLIC_KEY;

const formatArs = (n: number) =>
  `$${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

type Phase = 'idle' | 'success' | 'error';

export const RebillTest: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [raw, setRaw] = useState<unknown>(null);

  // Registro del SDK (solo en modo real).
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);

  // Mock (sin public key).
  const [mockBusy, setMockBusy] = useState(false);

  const checkoutRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!hasKey) return;
    let mounted = true;
    ensureRebillSDK()
      .then(() => mounted && setSdkReady(true))
      .catch((err) => mounted && setSdkError((err as Error)?.message ?? 'Error cargando Rebill.'));
    return () => {
      mounted = false;
    };
  }, []);

  // Cablea props + eventos del <rebill-checkout> cuando el SDK está listo y el
  // elemento montado (fase idle).
  useEffect(() => {
    if (!hasKey || !sdkReady || phase !== 'idle') return;
    const el = checkoutRef.current;
    if (!el) return;

    (el as any).publicKey = REBILL_PUBLIC_KEY;
    (el as any).language = 'es';
    if (REBILL_PLAN_ID) {
      (el as any).planId = REBILL_PLAN_ID;
    } else {
      (el as any).instantPlan = JSON.stringify(
        buildInstantPlan({
          amount: TOTAL_ARS,
          currency: 'ARS',
          title: 'Suscripción de prueba',
        }),
      );
    }

    const onSuccess = (e: Event) => {
      setRaw((e as CustomEvent).detail ?? null);
      setPhase('success');
    };
    const onError = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setMessage(
        (detail && (detail.message || detail.error)) ||
          'Hubo un error procesando el pago.',
      );
      setRaw(detail ?? null);
      // El componente maneja su propio estado de error; no lo desmontamos.
    };

    el.addEventListener('success', onSuccess);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('success', onSuccess);
      el.removeEventListener('error', onError);
    };
  }, [sdkReady, phase]);

  const reset = () => {
    setPhase('idle');
    setMessage(null);
    setRaw(null);
  };

  const handleMockSubscribe = async () => {
    setMockBusy(true);
    setMessage(null);
    await new Promise((r) => setTimeout(r, 900));
    setMockBusy(false);
    setRaw({ mock: true, amount: TOTAL_ARS, currency: 'ARS', interval: 'monthly' });
    setPhase('success');
  };

  return (
    <div className="min-h-full w-full flex items-start justify-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-brand-black">
            Suscripción Rebill
          </h1>
          <p className="mt-1 text-sm text-brand-black/60">
            Página de prueba de la integración con Rebill (Argentina / LATAM).
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-brand-black/10 bg-white p-6 shadow-sm">
          {!hasKey && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-amber-700">
              <FlaskConical className="h-4 w-4 shrink-0" />
              <span className="text-xs font-medium">
                Modo MOCK — falta VITE_REBILL_PUBLIC_KEY
              </span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-black/5">
              <CreditCard className="h-5 w-5 text-brand-black" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-black">Plan de prueba</p>
              <p className="text-xs text-brand-black/50">Cobro recurrente mensual</p>
            </div>
          </div>

          <div className="mt-5 flex items-baseline gap-1.5">
            <span className="text-4xl font-bold text-brand-black">
              {formatArs(TOTAL_ARS)}
            </span>
            <span className="text-sm text-brand-black/50">ARS / mes</span>
          </div>

          <div className="mt-6">
            {/* Resultado final */}
            {phase === 'success' && (
              <>
                <ResultBox
                  tone="success"
                  icon={<CheckCircle2 className="h-5 w-5" />}
                  title="¡Suscripción creada!"
                  detail={
                    hasKey
                      ? 'El pago fue procesado por Rebill.'
                      : 'Resultado simulado (mock). Pegá tu public key en VITE_REBILL_PUBLIC_KEY para el checkout real.'
                  }
                />
                <button
                  onClick={reset}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-brand-black/15 bg-white py-3 px-6 text-sm font-medium text-brand-black transition hover:bg-brand-black/[0.03]"
                >
                  <RefreshCw className="h-4 w-4" />
                  Probar de nuevo
                </button>
              </>
            )}

            {/* Modo real: checkout embebido de Rebill */}
            {phase === 'idle' && hasKey && (
              <>
                {message && (
                  <div className="mb-3">
                    <ResultBox
                      tone="error"
                      icon={<XCircle className="h-5 w-5" />}
                      title="Error de pago"
                      detail={message}
                    />
                  </div>
                )}
                {sdkError ? (
                  <ResultBox
                    tone="error"
                    icon={<XCircle className="h-5 w-5" />}
                    title="No se pudo cargar Rebill"
                    detail={sdkError}
                  />
                ) : !sdkReady ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-sm text-brand-black/50">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando checkout de Rebill…
                  </div>
                ) : (
                  <rebill-checkout ref={checkoutRef as any} />
                )}
              </>
            )}

            {/* Modo mock: botón simulado */}
            {phase === 'idle' && !hasKey && (
              <button
                onClick={handleMockSubscribe}
                disabled={mockBusy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-black py-3 px-6 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {mockBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Procesando…
                  </>
                ) : (
                  <>Suscribirse (simulado)</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Debug */}
        {raw != null && (
          <details className="mt-4 rounded-xl border border-brand-black/10 bg-brand-black/[0.02] p-3">
            <summary className="cursor-pointer text-xs font-medium text-brand-black/60">
              Ver respuesta (debug)
            </summary>
            <pre className="mt-2 overflow-x-auto text-[11px] leading-relaxed text-brand-black/70">
              {JSON.stringify(raw, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

function ResultBox({
  tone,
  icon,
  title,
  detail,
}: {
  tone: 'success' | 'error';
  icon: React.ReactNode;
  title: string;
  detail?: string;
}) {
  const styles =
    tone === 'success'
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-red-50 text-red-700';
  return (
    <div className={`flex items-start gap-3 rounded-xl px-4 py-3 ${styles}`}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {detail && <p className="mt-0.5 text-xs opacity-80">{detail}</p>}
      </div>
    </div>
  );
}

export default RebillTest;
